import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { prefetchProduct } from '@/lib/shopifyApi';
import { useInView } from '@/lib/useInView';
import { Heart } from 'lucide-react';
import { productImageProps } from '@/lib/responsiveProductImage';
import { useFavorites } from '@/context/FavoritesContext';
import { productUrl } from '@workspace/url-source';
import { ProductLabelOverlay } from '@/components/product/ProductLabels';
import { productUnavailableReason, type ProductLabelFields } from '@/lib/productLabels';

const GOLD = '#e05590';
const LINE = 'rgba(210,60,120,0.22)';
const DARK = '#2A1F14';
const MUTED = 'rgba(42,31,20,0.42)';

export interface ProductCardProps extends ProductLabelFields {
  image: string | null;
  name: string;
  price: string;
  handle?: string;
  variants?: Array<{ id: string; title: string; availableForSale: boolean }>;
  availability?: string;
  index?: number;
}

export function ProductCard({
  image,
  name,
  price,
  handle,
  variants = [],
  availability = 'Dostępny',
  index = 0,
  labels,
  purchaseBlocked,
  unavailableMessage,
}: ProductCardProps) {
  const labelFields = { labels, purchaseBlocked, unavailableMessage };
  const unavailableReason = productUnavailableReason(labelFields);
  const overlayFields = availability && !unavailableReason && !labels?.length
    ? {
      ...labelFields,
      labels: [{ id: 'legacy-availability', name: availability, color: GOLD, blocks_purchase: false, default_message: null }],
    }
    : labelFields;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [, navigate] = useLocation();
  const [hovered, setHovered] = useState(false);
  // Na urządzeniach dotykowych (brak wsparcia ':hover') przycisk "ZOBACZ WARIANTY"
  // dubluje dokładnie to, co robi już samo stuknięcie w zdjęcie — nawigację do
  // produktu — więc na mobile w ogóle go nie renderujemy zamiast pokazywać
  // zbędny, powielony CTA. Na desktopie zostaje jako hover-reveal bez zmian.
  const [isTouchDevice] = useState(
    () => typeof window !== 'undefined' && !window.matchMedia('(hover: hover)').matches,
  );
  const favorited = handle ? isFavorite(handle) : false;
  const { ref, inView } = useInView<HTMLDivElement>('-100px 0px');

  const productHref = handle ? productUrl(handle) : undefined;

  const imageContent = (
    <div className="relative aspect-square overflow-hidden bg-white rounded-sm transition-shadow duration-300 group-hover:shadow-[0_0_0_1.5px_rgba(210,60,120,0.7)]">
      {image ? (
        <img
          {...productImageProps(image)} decoding="async"
          alt={name}
          className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
      ) : (
        <div className="h-full w-full flex items-center justify-center bg-muted/50">
          <span className="text-muted-foreground text-sm">Brak zdjęcia</span>
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={ref}
      style={{
        opacity: inView ? undefined : 0,
        animationDelay: index > 0 ? `${Math.min(index * 0.1, 0.5)}s` : undefined,
      }}
      className={`group relative bk-labeled-product${inView ? ' bk-fade-up' : ''}`}
      data-testid={`card-product-${index}`}
      onMouseEnter={() => { setHovered(true); handle && prefetchProduct(handle); }}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative">
        {productHref ? (
          <Link href={productHref} style={{ textDecoration: 'none', display: 'block' }}>
            {imageContent}
          </Link>
        ) : imageContent}
        <ProductLabelOverlay product={overlayFields} />
        {handle && (
          <button
            type="button"
            onClick={e => { e.preventDefault(); e.stopPropagation(); toggleFavorite({ handle, title: name, image, price }); }}
            data-testid={`button-favorite-${index}`}
            className="absolute top-[10px] right-[10px] w-11 h-11 flex items-center justify-center rounded-full transition-all duration-200"
            style={{ background: favorited ? 'rgba(210,60,120,0.9)' : 'rgba(255,255,255,0.82)', backdropFilter: 'blur(6px)' }}
            aria-label={favorited ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
          >
            <Heart
              size={15}
              strokeWidth={1.8}
              style={{ color: favorited ? '#fff' : GOLD, fill: favorited ? '#fff' : 'none', transition: 'all 0.2s' }}
            />
          </button>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {productHref ? (
          <Link href={productHref} style={{ textDecoration: 'none', display: 'block' }}>
            <h3
              className="font-serif text-[23px] md:text-xl tracking-tight text-foreground hover:text-[#e05590] transition-colors"
              data-testid={`text-product-name-${index}`}
            >
              {name}
            </h3>
            <p
              className="mt-1 text-[21px] md:text-lg font-medium text-[#e05590] md:text-foreground/90"
              data-testid={`text-price-${index}`}
            >
              {price}
            </p>
          </Link>
        ) : (
          <div>
            <h3
              className="font-serif text-[23px] md:text-xl tracking-tight text-foreground"
              data-testid={`text-product-name-${index}`}
            >
              {name}
            </h3>
            <p
              className="mt-1 text-[21px] md:text-lg font-medium text-[#e05590] md:text-foreground/90"
              data-testid={`text-price-${index}`}
            >
              {price}
            </p>
          </div>
        )}

        {/* Add to cart — shows delivery hint. Desktop only, hover-reveal —
            on mobile tapping the photo already navigates to the product. */}
        {!isTouchDevice && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={e => { e.preventDefault(); e.stopPropagation(); if (productHref) navigate(productHref); }}
              disabled={!productHref || variants.length === 0}
              data-testid={`button-add-to-cart-${index}`}
              style={{
                width: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                padding: '11px 16px',
                background: 'transparent',
                border: `1px solid ${LINE}`,
                cursor: productHref ? 'pointer' : 'default',
                fontFamily: 'DM Sans, sans-serif', fontSize: 11,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                color: MUTED,
                transition: 'all 0.2s ease',
                opacity: hovered ? ((!productHref || variants.length === 0) ? 0.4 : 1) : 0,
                transform: hovered ? 'translateY(0)' : 'translateY(4px)',
              }}
              onMouseEnter={e => { if (productHref) { (e.currentTarget as HTMLButtonElement).style.borderColor = GOLD; (e.currentTarget as HTMLButtonElement).style.color = DARK; } }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = LINE; (e.currentTarget as HTMLButtonElement).style.color = MUTED; }}
            >
              ZOBACZ WARIANTY
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
